import { useState } from 'react'
import type { WorkOrder } from '@/types/work-orders'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import { relativeTime, formatTimestamp, statusColor, statusLabel, priorityLabel } from '@/lib/work-order-utils'
import { approveBrainWorkOrder, rejectBrainWorkOrder } from '@/api/brain'

interface WorkOrderDetailProps {
  workOrder: WorkOrder
  onBack: () => void
  onUpdated: () => void
}

interface TimelineEntry {
  timestamp: string
  label: string
}

function buildTimeline(wo: WorkOrder): TimelineEntry[] {
  const entries: TimelineEntry[] = []

  if (wo.created_at) {
    entries.push({ timestamp: wo.created_at, label: 'Created' })
  }

  if (wo.approved_at) {
    const byLabel = wo.approved_by ? ` by ${wo.approved_by}` : ''
    entries.push({ timestamp: wo.approved_at, label: `Approved${byLabel}` })
  }

  if (wo.rejected_at) {
    const byLabel = wo.rejected_by ? ` by ${wo.rejected_by}` : ''
    entries.push({ timestamp: wo.rejected_at, label: `Rejected${byLabel}` })
  }

  if (wo.completed_at) {
    entries.push({ timestamp: wo.completed_at, label: 'Completed' })
  }

  return entries
}

export function WorkOrderDetail({ workOrder: wo, onBack, onUpdated }: WorkOrderDetailProps) {
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [approveNotes, setApproveNotes] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectInput, setShowRejectInput] = useState(false)

  const timeline = buildTimeline(wo)

  const isPriorityHighOrCritical =
    wo.priority?.toLowerCase() === 'critical' || wo.priority?.toLowerCase() === 'high'

  const handleApprove = async () => {
    setError(null)
    setActionLoading(true)
    try {
      await approveBrainWorkOrder(wo._doc_id || wo.id, approveNotes || undefined)
      onUpdated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!showRejectInput) {
      setShowRejectInput(true)
      return
    }
    setError(null)
    setActionLoading(true)
    try {
      await rejectBrainWorkOrder(wo._doc_id || wo.id, rejectReason)
      onUpdated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject')
    } finally {
      setActionLoading(false)
    }
  }

  const bodyContent = wo.body?.instructions || wo.body?.message || wo.subject

  return (
    <div>
      {/* Back button */}
      <div className="mb-4">
        <Button variant="ghost" onClick={onBack}>
          &larr; Back to list
        </Button>
      </div>

      {/* Header section */}
      <div className="py-4 border-b border-swiss-gray-200">
        <div className="flex items-center gap-3">
          <span
            className={`inline-block px-2 py-0.5 border font-swiss-mono text-swiss-caption uppercase ${statusColor(wo.status)}`}
          >
            {statusLabel(wo.status)}
          </span>
          <span
            className={`font-swiss-mono text-swiss-caption uppercase ${
              isPriorityHighOrCritical ? 'text-swiss-red' : 'text-swiss-gray-500'
            }`}
          >
            {priorityLabel(wo.priority)}
          </span>
        </div>
        <h2 className="text-swiss-h2 font-swiss-sans font-semibold mt-2">
          {wo.subject}
        </h2>
        <div className="text-swiss-small text-swiss-gray-500 font-swiss-mono mt-1">
          from: {wo.from} &rarr; to: {wo.to}
        </div>
        <div className="text-swiss-caption text-swiss-gray-400 mt-1">
          {relativeTime(wo.created_at)}
        </div>
      </div>

      {/* Body / Instructions */}
      <div className="py-4 border-b border-swiss-gray-200">
        <div className="text-swiss-label font-swiss-mono uppercase tracking-[0.1em] text-swiss-gray-500 mb-2">
          Instructions
        </div>
        <div className="text-swiss-body font-swiss-sans whitespace-pre-wrap">
          {bodyContent}
        </div>
      </div>

      {/* Notes */}
      {wo.notes && (
        <div className="py-4 border-b border-swiss-gray-200">
          <div className="text-swiss-label font-swiss-mono uppercase tracking-[0.1em] text-swiss-gray-500 mb-2">
            Notes
          </div>
          <div className="text-swiss-small text-swiss-gray-700">
            {wo.notes}
          </div>
        </div>
      )}

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="py-4 border-b border-swiss-gray-200">
          <div className="text-swiss-label font-swiss-mono uppercase tracking-[0.1em] text-swiss-gray-500 mb-3">
            Timeline
          </div>
          <div>
            {timeline.map((entry, i) => {
              const isLast = i === timeline.length - 1
              return (
                <div key={`${entry.label}-${entry.timestamp}`} className="relative pl-6 pb-3">
                  {/* Dot */}
                  <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-swiss-gray-300" />
                  {/* Connecting line */}
                  {!isLast && (
                    <div className="absolute left-[3px] top-3 bottom-0 w-px bg-swiss-gray-200" />
                  )}
                  <div className="font-swiss-mono text-swiss-caption text-swiss-gray-500">
                    {formatTimestamp(entry.timestamp)}
                  </div>
                  <div className="text-swiss-small">
                    {entry.label}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Result */}
      {wo.result && (
        <div className="py-4 border-b border-swiss-gray-200">
          <div className="text-swiss-label font-swiss-mono uppercase tracking-[0.1em] text-swiss-gray-500 mb-2">
            Result
          </div>
          <pre className="font-swiss-mono text-swiss-caption bg-swiss-off-white border border-swiss-gray-200 p-3 overflow-x-auto whitespace-pre-wrap">
            {typeof wo.result === 'string' ? wo.result : JSON.stringify(wo.result, null, 2)}
          </pre>
        </div>
      )}

      {/* Actions — only for pending_approval */}
      {wo.status === 'pending_approval' && (
        <div className="py-4">
          <div className="flex flex-col gap-3">
            {/* Approve row */}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Input
                  label="Approval Notes"
                  placeholder="Notes (optional)"
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                />
              </div>
              <Button
                variant="primary"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading && !showRejectInput ? 'Approving...' : 'Approve'}
              </Button>
            </div>

            {/* Reject row */}
            <div className="flex items-end gap-3">
              {showRejectInput && (
                <div className="flex-1">
                  <Input
                    label="Rejection Reason"
                    placeholder="Reason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              )}
              <Button
                variant="secondary"
                onClick={handleReject}
                disabled={actionLoading || (showRejectInput && !rejectReason.trim())}
              >
                {showRejectInput
                  ? actionLoading
                    ? 'Rejecting...'
                    : 'Confirm Reject'
                  : 'Reject'}
              </Button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="text-swiss-red text-swiss-small mt-2">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
